import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import contactPageService from '../utils/contactPageService';
import SEO from '../components/SEO';
import PageSeparator from '../components/PageSeparator';

// Memoized ContactPanel to prevent unnecessary re-renders
const ContactPanel = React.memo(function ContactPanel({ id, title, icon, children, activePanel, togglePanel }) {
  const isActive = activePanel === id;
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');
  const previouslyMounted = useRef(false);

  useEffect(() => {
    if (isActive) {
      previouslyMounted.current = true;
      if (contentRef.current) {
        setHeight(`${contentRef.current.scrollHeight}px`);
      }
    } else {
      setHeight('0px');
    }
  }, [isActive]);

  return (
    <div className="mb-6 rounded-lg overflow-hidden shadow-md bg-white">
      <button
        onClick={() => togglePanel(id)}
        className={`w-full flex items-center justify-between p-4 text-left ${
          isActive ? 'bg-emerald-800 text-white' : 'bg-white text-emerald-800'
        }`}
        style={{ transition: 'background-color 0.3s ease, color 0.3s ease' }}
        aria-expanded={isActive}
      >
        <div className="flex items-center">
          <div
            className={`mr-3 ${isActive ? 'text-white' : 'text-emerald-800'}`}
            style={{
              transform: isActive ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease'
            }}
          >
            {icon}
          </div>
          <h3
            className="text-lg font-bold"
            style={{
              transform: isActive ? 'translateX(4px)' : 'translateX(0)',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {title}
          </h3>
        </div>
        <svg
          style={{
            transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease'
          }}
          className={`w-5 h-5 ${isActive ? 'text-white' : 'text-emerald-800'}`}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      <div
        style={{
          height,
          transition: 'height 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden'
        }}
      >
        <div
          ref={contentRef}
          style={{
            opacity: isActive ? 1 : 0,
            transform: isActive ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
            transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
          className={`p-6 border-t border-gray-100 ${isActive ? 'panel-content-active' : ''}`}
        >
          {(isActive || previouslyMounted.current) && children}
        </div>
      </div>
    </div>
  );
});

const Contact = () => {
  const { t } = useTranslation();
  const [activePanel, setActivePanel] = useState(null);

  const [callbackState, setCallbackState] = useState({
    name: '',
    phone: '',
    bestTime: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const togglePanel = (panelId) => {
    setActivePanel(activePanel === panelId ? null : panelId);
  };

  const handleCallbackChange = (e) => {
    const { name, value } = e.target;
    setCallbackState((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateCallbackForm = () => {
    const newErrors = {};
    if (!callbackState.name.trim()) {
      newErrors.callbackName = t('name_required');
    }
    if (!callbackState.phone.trim()) {
      newErrors.callbackPhone = t('phone_required');
    }
    if (!callbackState.bestTime.trim()) {
      newErrors.callbackTime = t('time_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!validateCallbackForm()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError(false);
    try {
      await contactPageService.requestCallback(callbackState);
      setCallbackState({
        name: '',
        phone: '',
        bestTime: '',
      });
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="text-gray-900">
      <SEO 
        title="AlzijDigi | Neem Contact Op"
        description="Neem contact op met AlzijDigi voor op maat gemaakte software ontwikkeling, webapplicaties en digitale oplossingen. Laten we bespreken hoe we uw bedrijf kunnen laten groeien."
        canonicalUrl="/contact"
      />
      <div className="min-h-screen pt-24 md:pt-36 lg:pt-40 pb-8 md:pb-16 px-2 md:px-8 lg:px-16">
        <style>
          {`
            .panel-content-active > form > div {
              animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              opacity: 0;
            }
            .panel-content-active > form > div:nth-child(1) { animation-delay: 0.1s; }
            .panel-content-active > form > div:nth-child(2) { animation-delay: 0.15s; }
            .panel-content-active > form > div:nth-child(3) { animation-delay: 0.2s; }
            .panel-content-active > form > div:nth-child(4) { animation-delay: 0.25s; }
            .panel-content-active > form > div:nth-child(5) { animation-delay: 0.3s; }
            .panel-content-active > form > div:nth-child(6) { animation-delay: 0.35s; }
            .panel-content-active > form > div.grid > div {
              animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              opacity: 0;
            }
            .panel-content-active > form > div.grid > div:nth-child(1) { animation-delay: 0.12s; }
            .panel-content-active > form > div.grid > div:nth-child(2) { animation-delay: 0.18s; }
            .panel-content-active > form > div.grid > div:nth-child(3) { animation-delay: 0.24s; }
            .panel-content-active > form > div.grid > div:nth-child(4) { animation-delay: 0.3s; }
            .panel-content-active > div > div {
              animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              opacity: 0;
            }
            .panel-content-active > div > div:nth-child(1) { animation-delay: 0.1s; }
            .panel-content-active > div > button { 
              animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              animation-delay: 0.2s;
              opacity: 0;
            }
            .panel-content-active > form > div.bg-red-50 {
              animation: fadeIn 0.4s ease-out forwards;
            }
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(15px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            .panel-content-active p.text-red-600 {
              animation: none !important;
              opacity: 1 !important;
            }
          `}
        </style>
        <div className="max-w-6xl mx-auto mt-8 md:mt-16">
          <h1
            className="text-lg sm:text-xl md:text-3xl font-bold mb-2 md:mb-4 text-emerald-900 text-center md:text-left"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            {t('contact_us')}
          </h1>
          <p className="text-sm sm:text-base md:text-lg mb-4 md:mb-8 text-gray-600 text-center md:text-left">
            {t('contact_page_description')}
          </p>
          
          {/* This is the flex container for the two-column layout */}
          <div className="flex text-left flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Left column - Contact info */}
            <div className="lg:w-1/3">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-6 md:mb-8">
                <h2 className="text-lg md:text-2xl font-bold mb-3 md:mb-4 text-emerald-900">{t('get_in_touch')}</h2>
                <p className="mb-4 md:mb-6 text-gray-700">
                  {t('contact_description')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full mr-4">
                      <svg className="w-5 h-5 text-emerald-800" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">{t('phone')}</h3>
                      <a
                        href="tel:+31687797600"
                        className="text-gray-700 underline underline-offset-2 hover:text-emerald-700 transition-colors"
                      >
                        +31 6 87797600
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full mr-4">
                      <svg className="w-5 h-5 text-emerald-800" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">{t('email')}</h3>
                      <a
                        href="mailto:info@alzijdigi.com"
                        className="text-gray-700 underline underline-offset-2 hover:text-emerald-700 transition-colors"
                      >
                        info@alzijdigi.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right column - Contact forms */}
            <div className="lg:w-2/3">
              {submitSuccess && (
                <div className="bg-green-50 p-6 rounded-lg text-center mb-6 shadow-md">
                  <svg className="w-16 h-16 text-emerald-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 className="text-2xl font-bold text-emerald-900 mb-2">{t('thank_you')}</h3>
                  <p className="text-emerald-700 mb-4">{t('contact_success')}</p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="px-6 py-2 bg-emerald-800 text-white rounded-md hover:bg-emerald-700 transition-colors duration-300"
                  >
                    {t('continue')}
                  </button>
                </div>
              )}
              
              <ContactPanel
                id="callback"
                activePanel={activePanel}
                togglePanel={togglePanel}
                title={t('request_callback')}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                }
              >
                <form onSubmit={handleCallbackSubmit} className="space-y-4">
                  {submitError && (
                    <div className="bg-red-50 p-4 rounded-md mb-4">
                      <p className="text-red-700">{t('error_message')}</p>
                    </div>
                  )}
                  <div>
                    <label htmlFor="callbackName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('your_name')} *
                    </label>
                    <input
                      type="text"
                      id="callbackName"
                      name="name"
                      value={callbackState.name}
                      onChange={handleCallbackChange}
                      className={`w-full px-4 py-2 border ${errors.callbackName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                    />
                    {errors.callbackName && <p className="mt-1 text-sm text-red-600">{errors.callbackName}</p>}
                  </div>
                  <div>
                    <label htmlFor="callbackPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('your_phone')} *
                    </label>
                    <input
                      type="tel"
                      id="callbackPhone"
                      name="phone"
                      value={callbackState.phone}
                      onChange={handleCallbackChange}
                      className={`w-full px-4 py-2 border ${errors.callbackPhone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                    />
                    {errors.callbackPhone && <p className="mt-1 text-sm text-red-600">{errors.callbackPhone}</p>}
                  </div>
                  <div>
                    <label htmlFor="bestTime" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('best_time_to_call')} *
                    </label>
                    <select
                      id="bestTime"
                      name="bestTime"
                      value={callbackState.bestTime}
                      onChange={handleCallbackChange}
                      className={`w-full px-4 py-2 border ${errors.callbackTime ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                    >
                      <option value="">{t('select_time')}</option>
                      <option value="morning">{t('morning')}</option>
                      <option value="afternoon">{t('afternoon')}</option>
                      <option value="evening">{t('evening')}</option>
                    </select>
                    {errors.callbackTime && <p className="mt-1 text-sm text-red-600">{errors.callbackTime}</p>}
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-6 py-3 text-white rounded-md transition-colors duration-300 ${isSubmitting ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-800 hover:bg-emerald-700'}`}
                      style={{ fontFamily: "'Space Mono', monospace" }}
                    >
                      {isSubmitting ? t('sending') : t('request_callback')}
                    </button>
                  </div>
                </form>
              </ContactPanel>
              
              <ContactPanel
                id="chat"
                activePanel={activePanel}
                togglePanel={togglePanel}
                title={t('chat_with_us')}
                icon={
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                }
              >
                <div className="text-center p-4">
                  <div className="mb-6">
                    <svg className="w-16 h-16 text-emerald-500 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                    <h3 className="text-xl font-bold text-emerald-900 mb-2">{t('chat_with_our_bot')}</h3>
                    <p className="text-gray-600 mb-6">
                      {t('chat_description')}
                    </p>
                  </div>
                  <button 
                    className="px-6 py-3 bg-emerald-800 text-white rounded-md hover:bg-emerald-700 transition-colors duration-300 w-full md:w-auto"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    {t('start_chat')}
                  </button>
                </div>
              </ContactPanel>
            </div>
          </div>
        </div>
      </div>
      <PageSeparator />
    </main>
  );
};

export default Contact;